import {
  IPD_DEFAULT_VITALS_PARAMETERS,
  IPD_MED_ROUTES,
  IPD_ORDER_MESSAGES,
  IPD_SCHEDULE_HORIZON_HOURS,
  createIpdOrderSchema,
  ipdIvDurationMinutes,
  ipdOrderDetailsSchema,
  ipdOrderEndsAt,
  ipdOrderProblems,
  ipdTransfusionMinutesPerUnit,
  localDateOf,
  parseIpdTaskLabel,
  planOrderTasks,
  type CreateIpdOrderInput,
  type DietType,
  type IPD_DIET_TEXTURES,
  type IPD_OXYGEN_DEVICES,
  type IPD_PHYSIOTHERAPY_TYPES,
  type IPD_VITALS_PARAMETERS,
  type IpdBloodProduct,
  type IpdConsultUrgency,
  type IpdFrequencyCode,
  type IpdMedRoute,
  type IpdOrderDetailsInput,
  type IpdOrderKind,
  type IpdOrderPriority,
  type IpdScheduleSettings,
  type IpdTaskKind,
  type IpdTransfusionStep,
  type ResolvedEmrForm,
} from '@his/shared';

export const TYPED_ORDER_KINDS = ['medication', 'iv_fluid', 'diet', 'nursing', 'blood_transfusion', 'oxygen', 'physiotherapy', 'consult', 'vitals_monitoring'] as const;
export type TypedOrderKind = (typeof TYPED_ORDER_KINDS)[number];

export type DietTexture = (typeof IPD_DIET_TEXTURES)[number];
export type OxygenDevice = (typeof IPD_OXYGEN_DEVICES)[number];
export type PhysiotherapyType = (typeof IPD_PHYSIOTHERAPY_TYPES)[number];
export type VitalsParameter = (typeof IPD_VITALS_PARAMETERS)[number];

export type OrderSection = 'investigations' | 'procedures';

export const ORDER_FORM_KINDS: Readonly<Record<string, IpdOrderKind>> = Object.freeze({
  medication_order: 'medication',
  iv_fluid_order: 'iv_fluid',
  diet_order: 'diet',
  nursing_order: 'nursing',
  blood_transfusion_order: 'blood_transfusion',
  oxygen_order: 'oxygen',
  physiotherapy_order: 'physiotherapy',
  consult_order: 'consult',
  vitals_monitoring_order: 'vitals_monitoring',
  lab_order: 'lab',
  radiology_order: 'radiology',
  procedure_order: 'procedure',
});

const SECTION_OF_KIND: Readonly<Partial<Record<IpdOrderKind, OrderSection>>> = Object.freeze({
  lab: 'investigations',
  radiology: 'investigations',
  procedure: 'procedures',
});

export interface ChooserEntry {
  kind: IpdOrderKind;
  formKey: string;
  required: boolean;
  section: OrderSection | null;
}

export function isTypedOrderKind(value: string | null | undefined): value is TypedOrderKind {
  return (TYPED_ORDER_KINDS as readonly string[]).includes(value ?? '');
}

export function orderChooserEntries(forms: readonly ResolvedEmrForm[], hidden: ReadonlySet<IpdOrderKind> = new Set()): ChooserEntry[] {
  const ordered = forms.map((form, index) => ({ form, index })).sort((a, b) => a.form.sortOrder - b.form.sortOrder || a.index - b.index);
  const entries: ChooserEntry[] = [];
  const seen = new Set<IpdOrderKind>();
  for (const { form } of ordered) {
    const kind = ORDER_FORM_KINDS[form.key];
    if (!form.enabled || !kind || hidden.has(kind) || seen.has(kind)) continue;
    seen.add(kind);
    entries.push({ kind, formKey: form.key, required: form.required, section: SECTION_OF_KIND[kind] ?? null });
  }
  return entries;
}

export function chosenOrderKind(value: string | null, entries: readonly ChooserEntry[]): TypedOrderKind | null {
  if (!isTypedOrderKind(value)) return null;
  return entries.some((entry) => entry.kind === value && entry.section === null) ? value : null;
}

const MINUTES_PER_HOUR = 60;

function positive(value: number): boolean {
  return Number.isFinite(value) && value > 0;
}

function roundTo(value: number, places: number): number {
  const factor = 10 ** places;
  return Math.round(value * factor) / factor;
}

export function ivDurationFromRate(volumeMl: number, rateMlPerHour: number): number | null {
  if (!positive(volumeMl) || !positive(rateMlPerHour)) return null;
  return ipdIvDurationMinutes({ volumeMl, rateMlPerHour });
}

export function ivRateFromDuration(volumeMl: number, durationMinutes: number): number | null {
  if (!positive(volumeMl) || !positive(durationMinutes)) return null;
  return roundTo((volumeMl * MINUTES_PER_HOUR) / durationMinutes, 1);
}

export function transfusionMinutesFromRate(product: IpdBloodProduct, volumeMlPerUnit: number, rateMlPerHour: number): number | null {
  if (!positive(volumeMlPerUnit) || !positive(rateMlPerHour)) return null;
  return ipdTransfusionMinutesPerUnit({ product, volumeMlPerUnit, rateMlPerHour });
}

export function transfusionRateFromMinutes(volumeMlPerUnit: number, minutesPerUnit: number): number | null {
  if (!positive(volumeMlPerUnit) || !positive(minutesPerUnit)) return null;
  return roundTo((volumeMlPerUnit * MINUTES_PER_HOUR) / minutesPerUnit, 1);
}

export function splitMinutes(total: number): { hours: number; minutes: number } {
  const whole = Number.isFinite(total) && total > 0 ? Math.round(total) : 0;
  return { hours: Math.floor(whole / MINUTES_PER_HOUR), minutes: whole % MINUTES_PER_HOUR };
}

export function numberOf(text: string): number | undefined {
  const trimmed = text.trim();
  if (!trimmed) return undefined;
  return Number(trimmed.replace(',', '.'));
}

export function minutesOf(hours: string, minutes: string): number | undefined {
  const h = numberOf(hours);
  const m = numberOf(minutes);
  if (h === undefined && m === undefined) return undefined;
  return Math.round((h ?? 0) * MINUTES_PER_HOUR + (m ?? 0));
}

function text(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed ? trimmed : undefined;
}

export interface CommonDraft {
  priority: IpdOrderPriority;
  startsAt: string;
  endsAt: string;
  instructions: string;
}

export const INITIAL_COMMON: Readonly<CommonDraft> = Object.freeze({ priority: 'routine', startsAt: '', endsAt: '', instructions: '' });

export type ScheduleTiming = 'frequency' | 'interval';
export type MedicationEnd = 'open' | 'days' | 'time';
export type RateSetting = 'rate' | 'duration';

export interface MedicationDraft {
  kind: 'medication';
  productId: string | null;
  drugName: string;
  controlled: boolean;
  dose: string;
  doseUnit: string;
  route: IpdMedRoute | '';
  timing: ScheduleTiming;
  frequency: IpdFrequencyCode | '';
  intervalHours: string;
  intervalMins: string;
  prn: boolean;
  prnIndication: string;
  prnMaxDosesPerDay: string;
  firstDoseAt: string;
  endMode: MedicationEnd;
  durationDays: string;
  isHighAlert: boolean;
}

export interface IvAdditiveDraft {
  id: string;
  name: string;
  amount: string;
}

export interface IvFluidDraft {
  kind: 'iv_fluid';
  productId: string | null;
  fluid: string;
  volumeMl: string;
  setBy: RateSetting;
  rateMlPerHour: string;
  durationHours: string;
  durationMins: string;
  additives: IvAdditiveDraft[];
  bags: number;
  continuous: boolean;
  checkIntervalMinutes: number;
  firstBagAt: string;
  controlled: boolean;
  isHighAlert: boolean;
}

export interface DietDraft {
  kind: 'diet';
  dietType: DietType | '';
  texture: DietTexture;
  fluidRestrictionMlPerDay: string;
  npoFrom: string;
  npoUntil: string;
  notes: string;
}

export interface NursingDraft {
  kind: 'nursing';
  task: string;
  timing: ScheduleTiming;
  frequency: IpdFrequencyCode | '';
  intervalHours: string;
  intervalMins: string;
}

export interface TransfusionDraft {
  kind: 'blood_transfusion';
  product: IpdBloodProduct | '';
  units: number;
  crossmatchRef: string;
  volumeMlPerUnit: string;
  setBy: RateSetting;
  rateMlPerHour: string;
  durationMinutesPerUnit: string;
  indication: string;
  firstUnitAt: string;
}

export interface OxygenDraft {
  kind: 'oxygen';
  device: OxygenDevice | '';
  flowLpm: string;
  fio2: string;
  targetSpo2Min: string;
  targetSpo2Max: string;
  checkIntervalMinutes: number;
}

export interface PhysiotherapyDraft {
  kind: 'physiotherapy';
  type: PhysiotherapyType | '';
  sessionsPerDay: number;
  durationDays: string;
  notes: string;
}

export type ConsultTarget = 'doctor' | 'department';

export interface ConsultDraft {
  kind: 'consult';
  target: ConsultTarget;
  targetDoctorId: string;
  targetDepartmentId: string;
  specialty: string;
  urgency: IpdConsultUrgency;
  question: string;
}

export interface VitalsMonitoringDraft {
  kind: 'vitals_monitoring';
  intervalMinutes: string;
  custom: boolean;
  parameters: VitalsParameter[];
}

export type OrderDraft =
  | MedicationDraft
  | IvFluidDraft
  | DietDraft
  | NursingDraft
  | TransfusionDraft
  | OxygenDraft
  | PhysiotherapyDraft
  | ConsultDraft
  | VitalsMonitoringDraft;

export type DraftOf<K extends TypedOrderKind> = Extract<OrderDraft, { kind: K }>;

export const IV_CHECK_INTERVALS = [30, 60, 120, 240] as const;
export const OXYGEN_CHECK_INTERVALS = [60, 120, 240, 480] as const;
export const VITALS_INTERVALS = [30, 60, 120, 240, 360, 480, 720] as const;
export const IV_VOLUME_CHOICES = [100, 250, 500, 1000] as const;
export const FLUID_LIMIT_CHOICES = [1000, 1500, 2000] as const;
export const DOSE_UNITS = ['mg', 'g', 'mcg', 'ml', 'units', 'IU', 'tab', 'cap', 'puff', 'drop'] as const;
export const COMMON_ROUTES: readonly IpdMedRoute[] = ['oral', 'iv', 'im', 'sc', 'inhaled', 'topical'];
export const OTHER_ROUTES: readonly IpdMedRoute[] = IPD_MED_ROUTES.filter((route) => !COMMON_ROUTES.includes(route));
export const FIO2_DEVICES: readonly OxygenDevice[] = ['venturi_mask', 'high_flow_nasal', 'cpap', 'bipap', 'ventilator'];
export const SPO2_PRESETS = [
  { min: 88, max: 92 },
  { min: 94, max: 98 },
] as const;

export function initialDraft<K extends TypedOrderKind>(kind: K): DraftOf<K>;
export function initialDraft(kind: TypedOrderKind): OrderDraft {
  switch (kind) {
    case 'medication':
      return {
        kind,
        productId: null,
        drugName: '',
        controlled: false,
        dose: '',
        doseUnit: '',
        route: '',
        timing: 'frequency',
        frequency: '',
        intervalHours: '',
        intervalMins: '',
        prn: false,
        prnIndication: '',
        prnMaxDosesPerDay: '',
        firstDoseAt: '',
        endMode: 'open',
        durationDays: '',
        isHighAlert: false,
      };
    case 'iv_fluid':
      return {
        kind,
        productId: null,
        fluid: '',
        volumeMl: '',
        setBy: 'rate',
        rateMlPerHour: '',
        durationHours: '',
        durationMins: '',
        additives: [],
        bags: 1,
        continuous: false,
        checkIntervalMinutes: 60,
        firstBagAt: '',
        controlled: false,
        isHighAlert: false,
      };
    case 'diet':
      return { kind, dietType: '', texture: 'regular', fluidRestrictionMlPerDay: '', npoFrom: '', npoUntil: '', notes: '' };
    case 'nursing':
      return { kind, task: '', timing: 'frequency', frequency: 'once', intervalHours: '', intervalMins: '' };
    case 'blood_transfusion':
      return { kind, product: '', units: 1, crossmatchRef: '', volumeMlPerUnit: '', setBy: 'duration', rateMlPerHour: '', durationMinutesPerUnit: '', indication: '', firstUnitAt: '' };
    case 'oxygen':
      return { kind, device: '', flowLpm: '', fio2: '', targetSpo2Min: '94', targetSpo2Max: '98', checkIntervalMinutes: 240 };
    case 'physiotherapy':
      return { kind, type: '', sessionsPerDay: 1, durationDays: '', notes: '' };
    case 'consult':
      return { kind, target: 'doctor', targetDoctorId: '', targetDepartmentId: '', specialty: '', urgency: 'routine', question: '' };
    case 'vitals_monitoring':
      return { kind, intervalMinutes: '240', custom: false, parameters: [...IPD_DEFAULT_VITALS_PARAMETERS] };
  }
}

export interface OrderRequestCandidate {
  priority: IpdOrderPriority;
  startsAt?: string;
  endsAt?: string;
  instructions?: string;
  details: { kind: IpdOrderKind } & Record<string, unknown>;
}

function scheduleOf(draft: { timing: ScheduleTiming; frequency: IpdFrequencyCode | ''; intervalHours: string; intervalMins: string }): { frequency?: IpdFrequencyCode; intervalMinutes?: number } {
  if (draft.timing === 'interval') return { intervalMinutes: minutesOf(draft.intervalHours, draft.intervalMins) };
  return { frequency: draft.frequency || undefined };
}

export function orderEndsAtApplies(draft: OrderDraft): boolean {
  switch (draft.kind) {
    case 'medication':
      return draft.endMode === 'time';
    case 'iv_fluid':
      return draft.continuous;
    case 'diet':
    case 'nursing':
    case 'oxygen':
    case 'vitals_monitoring':
      return true;
    default:
      return false;
  }
}

export function orderDetails(draft: OrderDraft): OrderRequestCandidate['details'] {
  switch (draft.kind) {
    case 'medication': {
      const schedule = draft.prn ? {} : scheduleOf(draft);
      return {
        kind: 'medication',
        productId: draft.productId ?? undefined,
        drugName: draft.drugName.trim(),
        dose: numberOf(draft.dose),
        doseUnit: draft.doseUnit.trim(),
        route: draft.route || undefined,
        ...schedule,
        prn: draft.prn,
        prnIndication: draft.prn ? text(draft.prnIndication) : undefined,
        prnMaxDosesPerDay: draft.prn ? numberOf(draft.prnMaxDosesPerDay) : undefined,
        durationDays: draft.endMode === 'days' ? numberOf(draft.durationDays) : undefined,
        firstDoseAt: draft.prn ? undefined : draft.firstDoseAt || undefined,
        isHighAlert: draft.isHighAlert || draft.controlled,
      };
    }
    case 'iv_fluid':
      return {
        kind: 'iv_fluid',
        productId: draft.productId ?? undefined,
        fluid: draft.fluid.trim(),
        volumeMl: numberOf(draft.volumeMl),
        rateMlPerHour: draft.setBy === 'rate' ? numberOf(draft.rateMlPerHour) : undefined,
        durationMinutes: draft.setBy === 'duration' ? minutesOf(draft.durationHours, draft.durationMins) : undefined,
        additives: draft.additives
          .filter((additive) => additive.name.trim() || additive.amount.trim())
          .map((additive) => ({ name: additive.name.trim(), amount: additive.amount.trim() })),
        bags: draft.continuous ? 1 : draft.bags,
        continuous: draft.continuous,
        checkIntervalMinutes: draft.checkIntervalMinutes,
        firstBagAt: draft.firstBagAt || undefined,
        isHighAlert: draft.isHighAlert || draft.controlled,
      };
    case 'diet':
      return {
        kind: 'diet',
        dietType: draft.dietType || undefined,
        texture: draft.texture,
        fluidRestrictionMlPerDay: numberOf(draft.fluidRestrictionMlPerDay),
        npoFrom: draft.npoFrom || undefined,
        npoUntil: draft.npoUntil || undefined,
        notes: text(draft.notes),
      };
    case 'nursing':
      return { kind: 'nursing', task: draft.task.trim(), ...scheduleOf(draft) };
    case 'blood_transfusion':
      return {
        kind: 'blood_transfusion',
        product: draft.product || undefined,
        units: draft.units,
        crossmatchRef: text(draft.crossmatchRef),
        volumeMlPerUnit: numberOf(draft.volumeMlPerUnit),
        rateMlPerHour: draft.setBy === 'rate' ? numberOf(draft.rateMlPerHour) : undefined,
        durationMinutesPerUnit: draft.setBy === 'duration' ? numberOf(draft.durationMinutesPerUnit) : undefined,
        indication: text(draft.indication),
        firstUnitAt: draft.firstUnitAt || undefined,
      };
    case 'oxygen':
      return {
        kind: 'oxygen',
        device: draft.device || undefined,
        flowLpm: numberOf(draft.flowLpm),
        fio2: numberOf(draft.fio2),
        targetSpo2Min: numberOf(draft.targetSpo2Min),
        targetSpo2Max: numberOf(draft.targetSpo2Max),
        checkIntervalMinutes: draft.checkIntervalMinutes,
      };
    case 'physiotherapy':
      return {
        kind: 'physiotherapy',
        type: draft.type || undefined,
        sessionsPerDay: draft.sessionsPerDay,
        durationDays: numberOf(draft.durationDays),
        notes: text(draft.notes),
      };
    case 'consult':
      return {
        kind: 'consult',
        targetDoctorId: draft.target === 'doctor' ? draft.targetDoctorId || undefined : undefined,
        targetDepartmentId: draft.target === 'department' ? draft.targetDepartmentId || undefined : undefined,
        specialty: text(draft.specialty),
        urgency: draft.urgency,
        question: draft.question.trim(),
      };
    case 'vitals_monitoring':
      return { kind: 'vitals_monitoring', intervalMinutes: numberOf(draft.intervalMinutes), parameters: [...draft.parameters] };
  }
}

function withoutUndefined<T extends Record<string, unknown>>(value: T): T {
  return Object.fromEntries(Object.entries(value).filter(([, entry]) => entry !== undefined)) as T;
}

export function buildOrderRequest(common: CommonDraft, draft: OrderDraft): OrderRequestCandidate {
  return withoutUndefined({
    priority: common.priority,
    startsAt: common.startsAt || undefined,
    endsAt: orderEndsAtApplies(draft) ? common.endsAt || undefined : undefined,
    instructions: text(common.instructions),
    details: withoutUndefined(orderDetails(draft)),
  });
}

export type OrderProblemCode = 'custom' | 'required' | 'too_short' | 'too_long' | 'too_small' | 'above' | 'too_big' | 'not_integer' | 'invalid';

export interface OrderProblem {
  path: string;
  field: string;
  code: OrderProblemCode;
  message: string;
  limit?: number;
}

export function fieldOfPath(path: string): string {
  const segments = path.split('.').filter(Boolean);
  if (segments[0] === 'details' && segments.length > 1) return segments[1] ?? '';
  return segments[0] ?? '';
}

interface IssueLike {
  code: string;
  path: (string | number)[];
  message: string;
  received?: unknown;
  expected?: unknown;
  type?: string;
  minimum?: number | bigint;
  maximum?: number | bigint;
  inclusive?: boolean;
}

function problemOfIssue(issue: IssueLike): OrderProblem {
  const path = issue.path.join('.');
  const base = { path, field: fieldOfPath(path), message: issue.message };
  const limit = (value: number | bigint | undefined) => (value === undefined ? undefined : Number(value));
  switch (issue.code) {
    case 'custom':
      return { ...base, code: 'custom' };
    case 'invalid_type':
      if (issue.received === 'undefined' || issue.received === 'null') return { ...base, code: 'required' };
      if (issue.expected === 'integer') return { ...base, code: 'not_integer' };
      return { ...base, code: 'invalid' };
    case 'invalid_enum_value':
      return { ...base, code: issue.received === '' || issue.received === undefined ? 'required' : 'invalid' };
    case 'too_small':
      if (issue.type === 'array' || (issue.type === 'string' && Number(issue.minimum) <= 1)) return { ...base, code: 'required' };
      if (issue.type === 'string') return { ...base, code: 'too_short', limit: limit(issue.minimum) };
      return { ...base, code: issue.inclusive === false ? 'above' : 'too_small', limit: limit(issue.minimum) };
    case 'too_big':
      return { ...base, code: issue.type === 'string' ? 'too_long' : 'too_big', limit: limit(issue.maximum) };
    default:
      return { ...base, code: 'invalid' };
  }
}

export interface OrderCheck {
  request: CreateIpdOrderInput | null;
  problems: OrderProblem[];
}

export function checkOrder(candidate: OrderRequestCandidate): OrderCheck {
  const parsed = createIpdOrderSchema.safeParse(candidate);
  const problems = parsed.success ? [] : parsed.error.issues.map((issue) => problemOfIssue(issue as IssueLike));
  for (const problem of ipdOrderProblems({ startsAt: candidate.startsAt, endsAt: candidate.endsAt, details: candidate.details as unknown as IpdOrderDetailsInput })) {
    if (!problems.some((known) => known.path === problem.path && known.message === problem.message)) {
      problems.push({ path: problem.path, field: fieldOfPath(problem.path), code: 'custom', message: problem.message });
    }
  }
  return { request: parsed.success && problems.length === 0 ? parsed.data : null, problems };
}

export function draftProblems(common: CommonDraft, draft: OrderDraft, now: Date = new Date()): OrderProblem[] {
  const problems: OrderProblem[] = [];
  const required = (field: string, path = `details.${field}`) => problems.push({ path, field, code: 'required', message: '' });
  if (draft.kind === 'medication' && draft.endMode === 'days' && !draft.durationDays.trim()) required('durationDays');
  if (draft.kind === 'medication' && draft.endMode === 'time' && !common.endsAt) required('endsAt', 'endsAt');
  if (orderEndsAtApplies(draft) && common.endsAt && !common.startsAt && Date.parse(common.endsAt) <= now.getTime()) {
    problems.push({ path: 'endsAt', field: 'endsAt', code: 'custom', message: IPD_ORDER_MESSAGES.endsBeforeStart });
  }
  if (draft.kind === 'blood_transfusion' && draft.setBy === 'rate') {
    if (!draft.rateMlPerHour.trim()) required('rateMlPerHour');
    if (!draft.volumeMlPerUnit.trim()) required('volumeMlPerUnit');
  }
  return problems;
}

export function checkDraft(common: CommonDraft, draft: OrderDraft, now: Date = new Date()): OrderCheck {
  const check = checkOrder(buildOrderRequest(common, draft));
  const extra = draftProblems(common, draft, now).filter((problem) => !check.problems.some((known) => known.field === problem.field));
  const problems = [...check.problems, ...extra];
  return { request: problems.length === 0 ? check.request : null, problems };
}

const TOUCH_ALIASES: Readonly<Record<string, readonly string[]>> = Object.freeze({
  intervalHours: ['intervalMinutes'],
  intervalMins: ['intervalMinutes'],
  durationHours: ['durationMinutes'],
  durationMins: ['durationMinutes'],
  timing: ['frequency', 'intervalMinutes'],
  setBy: ['rateMlPerHour', 'durationMinutes', 'durationMinutesPerUnit'],
  target: ['targetDoctorId', 'targetDepartmentId'],
  custom: ['intervalMinutes'],
  endMode: ['durationDays', 'endsAt'],
  prn: ['frequency', 'prnIndication'],
});

export function touchedFields(keys: readonly string[]): string[] {
  return [...new Set(keys.flatMap((key) => [key, ...(TOUCH_ALIASES[key] ?? [])]))];
}

const TIMING_PLACEHOLDERS: Readonly<Partial<Record<IpdOrderKind, Record<string, unknown>>>> = Object.freeze({
  medication: { drugName: 'medicine', dose: 1, doseUnit: 'unit', route: 'oral' },
  iv_fluid: { fluid: 'fluid' },
  nursing: { task: 'task' },
  oxygen: { device: 'nasal_cannula', flowLpm: 2, targetSpo2Min: 94, targetSpo2Max: 98 },
  physiotherapy: { type: 'other' },
  consult: { question: 'question', targetDepartmentId: '00000000-0000-4000-8000-000000000000' },
  diet: { dietType: 'normal' },
});

function previewDetails(details: OrderRequestCandidate['details']): OrderRequestCandidate['details'] {
  const filled: Record<string, unknown> = { ...details };
  for (const [key, value] of Object.entries(TIMING_PLACEHOLDERS[details.kind] ?? {})) {
    const current = filled[key];
    if (current === undefined || current === '' || (typeof current === 'number' && !Number.isFinite(current))) filled[key] = value;
  }
  return filled as OrderRequestCandidate['details'];
}

export interface PreviewEntry {
  at: Date;
  taskKind: IpdTaskKind;
  label: string;
  bag?: number;
  unit?: number;
  step?: IpdTransfusionStep;
  meal?: string;
  initial?: boolean;
}

export interface PreviewDay {
  date: string;
  at: Date;
  entries: PreviewEntry[];
}

export type PreviewState = 'scheduled' | 'as_needed' | 'no_tasks' | 'incomplete';

export interface SchedulePreview {
  state: PreviewState;
  from: Date;
  until: Date;
  days: PreviewDay[];
  shown: number;
  total: number;
  continues: boolean;
}

export const PREVIEW_CAP = 24;
const HOUR_MS = 3_600_000;

export interface PreviewOptions {
  now: Date;
  timeZone: string;
  settings?: IpdScheduleSettings;
  cap?: number;
}

export function schedulePreview(candidate: OrderRequestCandidate, options: PreviewOptions): SchedulePreview {
  const parsedStart = candidate.startsAt ? new Date(candidate.startsAt) : options.now;
  const from = Number.isNaN(parsedStart.getTime()) ? options.now : parsedStart;
  const until = new Date(from.getTime() + IPD_SCHEDULE_HORIZON_HOURS * HOUR_MS);
  const empty = (state: PreviewState): SchedulePreview => ({ state, from, until, days: [], shown: 0, total: 0, continues: false });
  if (candidate.details.kind === 'medication' && candidate.details.prn === true) return empty('as_needed');
  const parsed = ipdOrderDetailsSchema.safeParse(previewDetails(candidate.details));
  if (!parsed.success) return empty('incomplete');
  const details = parsed.data;
  const timingProblems = ipdOrderProblems({ startsAt: candidate.startsAt, endsAt: candidate.endsAt, details });
  if (timingProblems.length > 0) return empty('incomplete');
  const endsAt = ipdOrderEndsAt(from, candidate.endsAt ? new Date(candidate.endsAt) : null, details);
  const isStat = candidate.priority === 'stat' || (details.kind === 'medication' && details.frequency === 'stat');
  const plan = planOrderTasks(
    { kind: details.kind, priority: candidate.priority, isStat, startsAt: from, endsAt, details },
    { from, until, timeZone: options.timeZone, settings: options.settings },
  );
  if (plan.tasks.length === 0) return empty('no_tasks');
  const cap = Math.max(1, options.cap ?? PREVIEW_CAP);
  const days: PreviewDay[] = [];
  for (const task of plan.tasks.slice(0, cap)) {
    const date = localDateOf(task.scheduledAt, options.timeZone);
    const entry: PreviewEntry = { at: task.scheduledAt, taskKind: task.taskKind, label: task.label, ...parseIpdTaskLabel(task.label) };
    const day = days[days.length - 1];
    if (day && day.date === date) day.entries.push(entry);
    else days.push({ date, at: task.scheduledAt, entries: [entry] });
  }
  return {
    state: 'scheduled',
    from,
    until,
    days,
    shown: Math.min(cap, plan.tasks.length),
    total: plan.tasks.length,
    continues: plan.through !== null,
  };
}

export function allergyMatches(drugName: string, allergies: readonly { substance: string }[]): string[] {
  const name = drugName.trim().toLowerCase();
  if (name.length < 3) return [];
  const found: string[] = [];
  for (const allergy of allergies) {
    const substance = allergy.substance.trim();
    const needle = substance.toLowerCase();
    if (needle.length >= 3 && (name.includes(needle) || needle.includes(name)) && !found.includes(substance)) found.push(substance);
  }
  return found;
}

const PRESCRIPTION_TO_IPD_ROUTE: Readonly<Record<string, IpdMedRoute>> = Object.freeze({
  sublingual: 'sl',
  inhalation: 'inhaled',
  rectal: 'pr',
  vaginal: 'pv',
});

export function ipdRouteOf(route: string | null | undefined): IpdMedRoute | null {
  if (!route) return null;
  if ((IPD_MED_ROUTES as readonly string[]).includes(route)) return route as IpdMedRoute;
  return PRESCRIPTION_TO_IPD_ROUTE[route] ?? null;
}
